import { SavedSearch } from '../models';
import { locationCache } from './LocationCacheService';

// Define schema versions
const LATEST_SCHEMA_VERSION = 2;

// Define the structure for migration statistics
export interface MigrationStats {
  examined: number;
  repaired: number; // Includes migrations and minor fixes
  removed: number;
  apiCalls: number;
  apiFails: number;
  details: string[];
}

/**
 * Service responsible for migrating and validating saved search data
 * retrieved from storage to the latest schema version.
 */
class SavedSearchMigrator {

  /**
   * Migrates and validates raw search data from storage.
   * @param rawData The raw object potentially containing saved searches.
   * @returns An object containing the migrated searches (guaranteed latest version) and stats.
   */
  async migrateAndValidateAll(rawData: Record<string, any>): Promise<{
    migratedSearches: Record<string, SavedSearch>,
    stats: MigrationStats
  }> {
    const migratedSearches: Record<string, SavedSearch> = {};
    const stats: MigrationStats = {
      examined: 0,
      repaired: 0,
      removed: 0,
      apiCalls: 0,
      apiFails: 0,
      details: []
    };

    for (const id in rawData) {
      stats.examined++;
      let searchData = rawData[id];
      let isRepaired = false; // Tracks if any change was made (migration or fix)

      // 1. Basic Structure Validation
      if (!searchData || typeof searchData !== 'object') {
        stats.details.push(`Search ${id}: Invalid basic structure, removed.`);
        stats.removed++;
        continue; // Skip to next item
      }

      // 2. Determine Schema Version and Migrate if Necessary
      let currentVersion = searchData._schemaVersion;

      // --- Migration from Pre-Versioning/v1 to v2 ---
      if (!currentVersion) {
        // Assume pre-versioning format (check for old top-level properties)
        const potentialOldSearch = searchData as any;
        if ('locationId' in potentialOldSearch &&
            'startDate' in potentialOldSearch &&
            'services' in potentialOldSearch)
        {
          stats.details.push(`Search ${id}: Migrating from pre-versioning format to v${LATEST_SCHEMA_VERSION}.`);
          searchData.parameters = {
            locationId: potentialOldSearch.locationId,
            dateOfInterest: potentialOldSearch.startDate,
            services: potentialOldSearch.services,
          };
          delete potentialOldSearch.locationId;
          delete potentialOldSearch.startDate;
          delete potentialOldSearch.endDate;
          delete potentialOldSearch.services;
          searchData._schemaVersion = LATEST_SCHEMA_VERSION; // Mark as migrated
          currentVersion = LATEST_SCHEMA_VERSION;
          isRepaired = true;
        } else {
          // Cannot determine format or migrate, likely invalid
          stats.details.push(`Search ${id}: Pre-versioning format but missing expected old fields, removed.`);
          stats.removed++;
          continue;
        }
      } else if (currentVersion < LATEST_SCHEMA_VERSION) {
        // Placeholder for future migrations (e.g., v1 -> v2 if v1 was explicitly marked)
        stats.details.push(`Search ${id}: Schema version ${currentVersion} is outdated, but no migration path defined yet, removed.`);
        stats.removed++;
        continue;
      } else if (currentVersion > LATEST_SCHEMA_VERSION) {
        stats.details.push(`Search ${id}: Schema version ${currentVersion} is newer than supported (${LATEST_SCHEMA_VERSION}), removed.`);
        stats.removed++;
        continue;
      }

      // --- Validation and Repair for LATEST_SCHEMA_VERSION (v2) ---
      if (currentVersion === LATEST_SCHEMA_VERSION) {
        const search = searchData as SavedSearch; // Now we can assume v2 structure (mostly)

        // Repair missing ID
        if (!search.id) {
          search.id = id;
          isRepaired = true;
          stats.details.push(`Search ${id}: Added missing ID.`);
        }

        // Validate parameters object
        if (!search.parameters || typeof search.parameters !== 'object') {
          stats.details.push(`Search ${id}: Missing or invalid parameters object, removed.`);
          stats.removed++;
          continue;
        }

        // Validate dateOfInterest
        if (
          !search.parameters.dateOfInterest ||
          typeof search.parameters.dateOfInterest !== 'string' ||
          !/^\d{4}-\d{2}-\d{2}$/.test(search.parameters.dateOfInterest)
        ) {
          // Attempt fix from ISO datetime string
          if (typeof search.parameters.dateOfInterest === 'string' && search.parameters.dateOfInterest.includes('T')) {
            try {
              search.parameters.dateOfInterest = new Date(search.parameters.dateOfInterest).toISOString().split('T')[0];
              if (!/^\d{4}-\d{2}-\d{2}$/.test(search.parameters.dateOfInterest)) throw new Error("Invalid date format after conversion");
              stats.details.push(`Search ${id}: Converted dateOfInterest from datetime to date format.`);
              isRepaired = true;
            } catch (e) {
              stats.details.push(`Search ${id}: Invalid dateOfInterest format (${search.parameters.dateOfInterest}), removed.`);
              stats.removed++;
              continue;
            }
          } else {
            stats.details.push(`Search ${id}: Missing or invalid dateOfInterest format (${search.parameters.dateOfInterest}), removed.`);
            stats.removed++;
            continue;
          }
        }

        // Validate services array
        if (!Array.isArray(search.parameters.services) || search.parameters.services.length === 0) {
          stats.details.push(`Search ${id}: Missing or empty services array in parameters, removed.`);
          stats.removed++;
          continue;
        }

        // Validate locationId type
        if (!search.parameters.locationId || typeof search.parameters.locationId !== 'number') {
          stats.details.push(`Search ${id}: Missing or invalid locationId type in parameters (${search.parameters.locationId}), removed.`);
          stats.removed++;
          continue;
        }

        // Validate locationId existence via cache first, then API if needed
        try {
          // This will check cache first, only calling API if location not in cache
          const location = await locationCache.fetchIfNeeded(search.parameters.locationId);
          
          // Count as API call only if not found in cache
          if (!locationCache.has(search.parameters.locationId)) {
            stats.apiCalls++;
          }
          
          if (!location) {
            throw new Error('Location not found in cache or API');
          }
        } catch (error) {
          stats.apiFails++;
          console.error(`Location validation failed for ID ${search.parameters.locationId} in search ${id}:`, error);
          stats.details.push(`Search ${id}: Location ID ${search.parameters.locationId} failed validation. The search will be kept but marked as invalid.`);
          search.isValid = false;
        }

        // Add to results regardless of API validation
        migratedSearches[id] = search;
        if (isRepaired) {
          stats.repaired++;
        }

        // Repair missing createdAt
        if (!search.createdAt) {
          search.createdAt = new Date().toISOString();
          isRepaired = true;
          stats.details.push(`Search ${id}: Added missing creation timestamp.`);
        }

        // Repair missing autoSave (default to true)
        if (search.autoSave === undefined) {
          search.autoSave = true;
          isRepaired = true;
          stats.details.push(`Search ${id}: Set default autoSave value (true).`);
        }

        // If we reached here, the search is valid (or repaired) v2
        migratedSearches[id] = search; // Add to the results
        if (isRepaired) {
          stats.repaired++;
        }

      } // End v2 validation block
    } // End loop through rawData

    return { migratedSearches, stats };
  }
}

// Export a singleton instance
export const savedSearchMigrator = new SavedSearchMigrator();
